import React, {useState} from "react"
import {useWatch} from "react-hook-form"
import ObjectID from "bson-objectid"
import {AbsoluteCenter, Box, Button, ButtonGroup, Divider, FormLabel} from "@chakra-ui/react"
import {XMarkIcon} from "@heroicons/react/24/outline"

import {IQuestion, QuestionField} from "@backend/types"
import {FileUpload, Table} from "@components/elements"
import {FormInput, FSelect, Option} from "@components/form"
import {AirtableImport, AirtableImportType} from "@components/airtable/AirtableImport"

type QuestionSelectProps = {
    register: any
    errors?: any,
    setValue?: any,
    questions?: IQuestion[],
    options: Option[],
    questionFields?: QuestionField[],
    questionRefIdField?: string,
    control: any,
    onQuestionsChange: (any) => void
}


export default function QuestionSelect({
                                           register,
                                           errors,
                                           setValue,
                                           questions,
                                           questionFields,
                                           questionRefIdField,
                                           control,
                                           options = [],
                                           onQuestionsChange
                                       }: QuestionSelectProps) {
    const [records, setRecords] = useState<{
        headers: string[],
        selectedHeaders?: QuestionField[] | null,
        data?: any[]
    }>(null)


    const headersWatch: QuestionField[] = useWatch({
        name: "modeOptions.fields",
        control,
        defaultValue: questionFields
    })

    const questionRefWatch = useWatch({
        name: "modeOptions.questionRefIdField",
        control,
        defaultValue: questionRefIdField
    })

    function onTextUpload(records: any[]) {

        if (!records.length) {
            return
        }

        setRecords({
            headers: Object.keys(records[0]),
            selectedHeaders: null,
            data: records
        })
    }

    function onTextRemove(refId: string) {
        onQuestionsChange({questions: questions.filter(x => x.refId !== refId)})
    }

    function onMapCancel() {
        setRecords(null)
    }

    function onImport() {

        const selectedHeaders = headersWatch.filter(x => x.fileHeader && x.title)

        const questions = records.data.reduce((mainAcc, mainItem, i) => {
            let question: IQuestion = {
                _id: ObjectID().toHexString(),
                refId: questionRefWatch ? mainItem[questionRefWatch] : undefined,
                texts: selectedHeaders.map(x => ({
                    fieldId: x.keyword || x.fileHeader,
                    optionKey: x.optionKey,
                    text: mainItem[x.fileHeader],
                    refId: mainItem[x.refId],
                    isPinned: false
                }))
            }


            mainAcc.push(question)

            return mainAcc

        }, [])

        onQuestionsChange({
            questionFields: selectedHeaders,
            questionRefIdField: questionRefWatch,
            questions
        })

    }

    function onAirtableImport({headers, selectedHeaders, records}: AirtableImportType) {
        console.log("from airtable", {headers, records})
        setRecords({
            headers: headers.map(x => x.name),
            selectedHeaders,
            data: records
        })

        setValue("modeOptions.fields", selectedHeaders)
        setValue("modeOptions.questionRefIdField", "Record ID")

    }

    // function onHeaderSelect(value, e) {
    //     const selectedHeader = e.target.value
    //
    //     const selectedHeaders = {
    //         ...(records.selectedHeaders || {}),
    //         [value]: selectedHeader
    //     }
    //     setRecords({
    //         ...records,
    //         selectedHeaders
    //     })
    //
    //     if (Object.keys(selectedHeaders || {}).length === options.length) {
    //
    //         onTextsChange({
    //             texts: records.data.reduce((mainAcc, mainItem, i) => {
    //
    //                 const data = Object.keys(selectedHeaders || {})
    //                     .reduce((acc, headerKey) => {
    //                         acc[headerKey] = mainItem[selectedHeaders[headerKey]]
    //                         return acc
    //                     }, {})
    //
    //                 if (Object.values(data).filter(x => !x).length === 0) {
    //                     mainAcc.push({tempId: i.toString(), data})
    //                 }
    //
    //                 return mainAcc
    //             }, [])
    //         })
    //     }
    // }


    if (questions.length)
        return <div>
            <div className="flex justify-end mb-2">
                <Button
                    colorScheme="red"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        onQuestionsChange({
                            questions: []
                        })
                    }}
                    leftIcon={<XMarkIcon className="w-4 h-4"/>}
                >Clear Questions</Button>
            </div>
            <Table
                caption={`Total ${questions.length} Questions. ${questions.length > 10 ? "showing first 10" : ""} `}
                columns={[
                    ...headersWatch.filter(x => x.fileHeader && x.title)
                        .map(({fileHeader, title, keyword, optionKey}, i) => ({
                            header: title || optionKey,
                            id: `${fileHeader}_${i}`,
                            accessorKey: "refId",
                            enableColumnFilter: false,
                            enableSorting: false,
                            cell: ({row: {original}}) => <p
                                className="line-clamp-3 whitespace-break-spaces">
                                {(original.texts || []).find(x => x.optionKey === optionKey).text}
                            </p>
                        })),
                    {
                        id: "select-button",
                        cell: ({row: {original}}) => <Button
                            colorScheme="red"
                            variant="ghost"
                            size="sm"
                            onClick={onTextRemove.bind(null, original.refId)}
                        >Remove</Button>
                    }
                ]}
                data={questions.slice(0, 10)}
            />
        </div>

    if (records)
        return <div>
            <div className="border-b border-gray-200 pb-5 mb-5">
                <h3 className="text-base font-semibold leading-6 text-gray-900">Questions Import</h3>
                <p className="mt-2 max-w-4xl text-sm text-gray-500">
                    Choose and map the file headers to import your questions
                </p>
            </div>
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                    <FSelect
                        className="col-span-1"
                        register={register}
                        field="modeOptions.questionRefIdField"
                        errors={errors}
                        label="Question ID (optional)"
                        options={records.headers.map(x => ({label: x, value: x}))}/>
                </div>


                {options.map(({label, value}, optionIndex) =>
                    <div key={value} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <input type="hidden" {...register(`modeOptions.fields[${optionIndex}].optionKey`, {
                            value: value
                        })} />
                        <input type="hidden" {...register(`modeOptions.fields[${optionIndex}].isPinned`, {
                            value: value === "input",
                        })} />
                        <FSelect
                            className="col-span-1"
                            register={register}
                            field={`modeOptions.fields[${optionIndex}].fileHeader`}
                            errors={errors}
                            label={label}
                            options={records.headers.map(x => ({label: x, value: x}))}/>
                        <FSelect
                            className="col-span-1"
                            register={register}
                            field={`modeOptions.fields[${optionIndex}].refId`}
                            errors={errors}
                            label="Ref Id (optional)"
                            options={records.headers.map(x => ({label: x, value: x}))}/>
                        <FormInput className="col-span-1" register={register} errors={errors}
                                   field={`modeOptions.fields[${optionIndex}].title`}
                                   label="Title"/>
                        <FormInput className="col-span-1" register={register} errors={errors}
                                   field={`modeOptions.fields[${optionIndex}].keyword`}
                                   label="Keyword"/>
                    </div>
                )}


                <div className="flex justify-end">
                    <ButtonGroup size="sm" spacing="2">
                        <Button variant="outline" onClick={onMapCancel}>Cancel</Button>
                        <Button colorScheme="blue" onClick={onImport}>Import</Button>
                    </ButtonGroup>
                </div>
            </div>
        </div>


    return <div>
        <FileUpload
            register={register}
            errors={errors}
            accept=".csv"
            field="file"
            label="Texts"
            onFinish={onTextUpload}
        />
        <Box position="relative" padding="10">
            <Divider/>
            <AbsoluteCenter bg="white" px="4">
                OR
            </AbsoluteCenter>
        </Box>
        <FormLabel>Import From Airtable</FormLabel>

        <AirtableImport onImport={onAirtableImport}/>

    </div>
}